import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { COMPANY } from '../constants/company';
import { roleLabel } from '../types';

interface LayoutProps {
  children: React.ReactNode;
}

const adminNav = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊' },
  { to: '/clients', label: 'Clientes', icon: '👥' },
  // Fila do comercial (pendências de endereço do CNEFE). Só aqui, no menu de acesso total: a rota é `access="full"`.
  { to: '/enderecos', label: 'Endereços a corrigir', icon: '🏠' },
  { to: '/sellers', label: 'Vendedores', icon: '🧑‍💼' },
  { to: '/checkins', label: 'Check-ins no Mapa', icon: '🗺️' },
  { to: '/live', label: 'Mapa ao vivo', icon: '🛰️' },
  { to: '/replay', label: 'Replay do dia', icon: '📽️' },
  { to: '/jornada', label: 'Replay do dia de trabalho', icon: '🕒' },
  { to: '/roteirizacao', label: 'Roteirização', icon: '🧭' },
  { to: '/rotas', label: 'Rotas', icon: '🛣️' },
  { to: '/history', label: 'Histórico', icon: '📋' },
  { to: '/reports', label: 'Relatórios', icon: '📈' },
  { to: '/settings', label: 'Configurações', icon: '⚙️' },
];

// "Replay do dia de trabalho" NÃO entra no managerNav de propósito: é acesso total (isFullAccess);
// o gestor com escopo (gerente/coordenador/supervisor) não vê (jornada é dado sensível, LGPD).
// Gestor (gerente/coordenador/supervisor): vê tudo da hierarquia. Configurações aparece, mas a página
// mostra SÓ o "Sincronizar Clientes" para quem não é `admin` (as regras do app seguem exclusivas dele).
const managerNav = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊' },
  { to: '/clients', label: 'Clientes', icon: '👥' },
  { to: '/checkins', label: 'Check-ins no Mapa', icon: '🗺️' },
  { to: '/live', label: 'Mapa ao vivo', icon: '🛰️' },
  { to: '/replay', label: 'Replay do dia', icon: '📽️' },
  { to: '/roteirizacao', label: 'Roteirização', icon: '🧭' },
  { to: '/rotas', label: 'Rotas', icon: '🛣️' },
  { to: '/history', label: 'Histórico', icon: '📋' },
  { to: '/reports', label: 'Relatórios', icon: '📈' },
  { to: '/settings', label: 'Configurações', icon: '⚙️' },
];

const sellerNav = [
  { to: '/clients', label: 'Meus Clientes', icon: '👥' },
  { to: '/roteirizacao', label: 'Roteirização', icon: '🧭' },
  { to: '/history', label: 'Minhas Visitas', icon: '📋' },
  { to: '/settings', label: 'Configurações', icon: '⚙️' },
];

export default function Layout({ children }: LayoutProps) {
  const { user, isAdmin, isManager, logout } = useAuth();
  const navigate = useNavigate();
  // "Vendedores" é exclusiva do papel LITERAL `admin`. O `isAdmin` do contexto é `isFullAccess`, então
  // diretor/pricing/gerente_nacional caem no `adminNav` e precisam ter o item filtrado aqui.
  const isAdminRole = user?.role === 'admin';
  const navItems = (isAdmin ? adminNav : isManager ? managerNav : sellerNav)
    .filter((i) => i.to !== '/sellers' || isAdminRole);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt={COMPANY.name} style={styles.logo} />
          <div>
            <div style={styles.appName}>{COMPANY.appName}</div>
            <div style={styles.appSub}>{roleLabel(user?.role)}</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
              })}
            >
              <span style={{ marginRight: 10 }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{user?.name}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Sair</button>

          <div style={styles.companyInfo}>
            <div style={styles.companyName}>{COMPANY.name}</div>
            <div style={styles.companyDetail}>CNPJ: {COMPANY.cnpj}</div>
            <div style={styles.companyDetail}>{COMPANY.address}</div>
            <div style={styles.versionPill}>🌐 Web {COMPANY.version}</div>
          </div>
        </div>
      </aside>

      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', minHeight: '100vh', background: '#f5f6fa' },
  sidebar: {
    width: 240,
    background: '#c2410c',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '24px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  logo: { width: 40, height: 40, borderRadius: 10, objectFit: 'contain', background: '#fff' },
  appName: { fontWeight: 700, fontSize: 18, color: '#fff' },
  appSub: { fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 },
  nav: { flex: 1, padding: '16px 0', overflowY: 'auto' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '11px 20px',
    color: 'rgba(255,255,255,0.75)',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    borderLeft: '3px solid transparent',
    transition: 'all 0.15s',
  },
  navItemActive: {
    color: '#fff',
    background: 'rgba(255,255,255,0.1)',
    borderLeft: '3px solid #fdba74',
  },
  sidebarFooter: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  userInfo: { marginBottom: 10 },
  userName: { fontWeight: 600, fontSize: 13, color: '#fff' },
  userEmail: { fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 },
  logoutBtn: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6,
    padding: '6px 14px',
    fontSize: 13,
    cursor: 'pointer',
    width: '100%',
  },
  companyInfo: { marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.1)' },
  companyName: { fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.7)' },
  companyDetail: { fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3, lineHeight: 1.4 },
  versionPill: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.28)', borderRadius: 999, padding: '5px 12px', fontSize: 12, color: '#fff' },
  main: { flex: 1, marginLeft: 240, padding: 32, minHeight: '100vh' },
};
